import {useSearchParams} from "react-router-dom";
import dayjs from "dayjs";
import {usePeople} from "@/context/PeopleContext";
import type {PersonModelInterface} from "@/models/personModel";
import {useQueryPatch} from "@/hooks/useQueryPatch";
import {useEffect, useMemo, useRef, useState} from "react";

type SortDir = "" | "asc" | "desc";
export type RangeType = "all" | "year" | "month" | "week" | "custom";
export type TabType = "all" | "pending" | "today" | "overdue" | "archive";

function toNum(v: string | null, fallback: number) {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : fallback;
}

function ymd(d: Date) {
    return dayjs(d).format("YYYY-MM-DD");
}

function labelToTab(label: string): TabType {
    if (label === "В процессе") return "pending";
    if (label === "Выплаты на сегодня") return "today";
    if (label === "Просрочено") return "overdue";
    if (label === "Архив") return "archive";
    return "all";
}

function isTabKey(v: string): v is TabType {
    return v === "all" || v === "pending" || v === "today" || v === "overdue" || v === "archive";
}

export function usePeopleUI() {
    const {people, getPeople, deletePersonById, bulkDelete} = usePeople();
    const [searchParams, setSearchParams] = useSearchParams();
    const {patchParams} = useQueryPatch();

    useEffect(() => {
        const hasRange = searchParams.has("rangeType");
        const hasTab = searchParams.has("tab");
        if (hasRange && hasTab) return;

        setSearchParams(
            (prev) => {
                const p = new URLSearchParams(prev);
                if (!p.get("rangeType")) p.set("rangeType", "all");
                if (!p.get("tab")) p.set("tab", "all");
                if (!p.get("page")) p.set("page", "1");
                if (!p.get("pageSize")) p.set("pageSize", "10");
                return p;
            },
            {replace: true}
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tabParamRaw = searchParams.get("tab") || "all";
    const tab: TabType = isTabKey(tabParamRaw) ? tabParamRaw : labelToTab(tabParamRaw);

    useEffect(() => {
        if (!isTabKey(tabParamRaw)) {
            const normalized = labelToTab(tabParamRaw);
            if (normalized !== tabParamRaw) patchParams({tab: normalized}, {resetPage: true, replace: true});
        }
    }, [tabParamRaw, patchParams]);

    const page = toNum(searchParams.get("page"), 1);
    const pageSize = toNum(searchParams.get("pageSize"), 10);

    const sortBy = searchParams.get("sortBy") || "";
    const sortDirection = (searchParams.get("sortDirection") || "") as SortDir;

    const rangeType = (searchParams.get("rangeType") as RangeType) || "all";
    const search = searchParams.get("search") || "";

    // стабильные дефолты (важно: не создавать Date на каждом рендере)
    const defaultStartRef = useRef(dayjs().subtract(1, "year").startOf("day").toDate());
    const defaultEndRef = useRef(dayjs().endOf("day").toDate());

    const startParam = searchParams.get("start"); // строка
    const endParam = searchParams.get("end");     // строка

    // Строки для запроса, они стабильны если URL не менялся
    const startYmd = startParam || ymd(defaultStartRef.current);
    const endYmd = endParam || ymd(defaultEndRef.current);

    // Date только для UI инпутов
    const startDate = useMemo(() => dayjs(startYmd).toDate(), [startYmd]);
    const endDate = useMemo(() => dayjs(endYmd).toDate(), [endYmd]);

    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    // Запрос в базу
    useEffect(() => {
        getPeople({
            dateRange: tab !== "today" ? (rangeType === "all" ? undefined : rangeType) : undefined,
            customStart: tab !== "today" && rangeType === "custom" ? startDate : undefined,
            customEnd: tab !== "today" && rangeType === "custom" ? endDate : undefined,
            search,
        });
        // ВАЖНО: зависимости теперь стабильные
    }, [getPeople, tab, rangeType, startYmd, endYmd, search, startDate, endDate]);

    // Фильтрация по табам
    const filteredPeople: PersonModelInterface[] = useMemo(() => {
        return people.filter((person: any) => {
            const today = dayjs();
            const createdAt = dayjs(person.createdAt);
            const releaseDay = dayjs(person.creditDetail?.releaseDay);

            if (tab === "all") {
                if (
                    person.status === "closed" ||
                    person.status === "closedEarly" ||
                    person.status === "returned" ||
                    person.status === "overdue"
                ) return false;

                if (rangeType === "all") return true;

                if (rangeType === "custom") {
                    return (
                        (createdAt.isSame(startDate, "day") || createdAt.isAfter(startDate, "day")) &&
                        (createdAt.isSame(endDate, "day") || createdAt.isBefore(endDate, "day"))
                    );
                }

                return true;
            }

            if (tab === "pending") {
                if (person.status !== "pending") return false;
                if (rangeType === "all") return true;

                if (rangeType === "custom") {
                    return (
                        (createdAt.isSame(startDate, "day") || createdAt.isAfter(startDate, "day")) &&
                        (createdAt.isSame(endDate, "day") || createdAt.isBefore(endDate, "day"))
                    );
                }

                return true;
            }

            if (tab === "today") {
                const paymentDay = releaseDay.date();
                const selectedDay = today.date();
                return (
                    paymentDay === selectedDay &&
                    person.status !== "closed" &&
                    person.status !== "closedEarly" &&
                    person.status !== "returned"
                );
            }

            if (tab === "overdue") return person.status === "overdue" && releaseDay.isBefore(today, "day");

            if (tab === "archive") {
                const isArchive =
                    person.status === "closed" || person.status === "closedEarly" || person.status === "returned";
                if (!isArchive) return false;

                if (rangeType === "all") return true;

                if (rangeType === "custom") {
                    return (
                        (createdAt.isSame(startDate, "day") || createdAt.isAfter(startDate, "day")) &&
                        (createdAt.isSame(endDate, "day") || createdAt.isBefore(endDate, "day"))
                    );
                }

                return true;
            }

            return true;
        });
    }, [people, tab, rangeType, startDate, endDate]);

    const sortedPeople = useMemo(() => {
        if (!sortBy || !sortDirection) return filteredPeople;

        return [...filteredPeople].sort((a: any, b: any) => {
            let av: any;
            let bv: any;

            switch (sortBy) {
                case "name":
                    av = (a.name || "").toLowerCase();
                    bv = (b.name || "").toLowerCase();
                    break;
                case "total":
                    av = a.creditDetail?.total || 0;
                    bv = b.creditDetail?.total || 0;
                    break;
                case "releaseDay":
                    av = dayjs(a.creditDetail?.releaseDay).valueOf();
                    bv = dayjs(b.creditDetail?.releaseDay).valueOf();
                    break;
                default:
                    av = 0;
                    bv = 0;
            }

            if (av < bv) return sortDirection === "asc" ? -1 : 1;
            if (av > bv) return sortDirection === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredPeople, sortBy, sortDirection]);

    const tabTotalCount = sortedPeople.length;
    const totalPages = Math.max(1, Math.ceil(tabTotalCount / pageSize));

    const displayedPeople = useMemo(() => {
        const safePage = Math.min(Math.max(page, 1), totalPages);
        const startIndex = (safePage - 1) * pageSize;
        return sortedPeople.slice(startIndex, startIndex + pageSize);
    }, [sortedPeople, page, pageSize, totalPages]);

    useEffect(() => {
        if (page > totalPages) patchParams({page: String(totalPages)}, {replace: true});
    }, [page, totalPages, patchParams]);

    const getPageNumbers = () => {
        const maxPagesToShow = 5;
        const safePage = Math.min(Math.max(page, 1), totalPages);

        let start = Math.max(1, safePage - Math.floor(maxPagesToShow / 2));
        let end = Math.min(totalPages, start + maxPagesToShow - 1);

        if (end - start + 1 < maxPagesToShow) start = Math.max(1, end - maxPagesToShow + 1);

        const nums: number[] = [];
        for (let i = start; i <= end; i++) nums.push(i);
        return nums;
    };

    const actions = {
        setTab: (nextTab: TabType) => {
            setSelectedIds([]);
            patchParams({tab: nextTab}, {resetPage: true});
        },

        onSort: (key: string) => {
            if (sortBy !== key) return patchParams({sortBy: key, sortDirection: "asc"}, {resetPage: true});
            if (sortDirection === "asc") return patchParams({sortBy: key, sortDirection: "desc"}, {resetPage: true});
            patchParams({sortBy: "", sortDirection: ""}, {resetPage: true});
        },

        setPage: (nextPage: number) => {
            const clamped = Math.min(Math.max(nextPage, 1), totalPages);
            patchParams({page: String(clamped)});
        },

        setPageSize: (nextSize: number) => {
            setSelectedIds([]);
            patchParams({pageSize: String(nextSize)}, {resetPage: true});
        },

        prevPage: () => {
            if (page <= 1) return;
            patchParams({page: String(page - 1)});
        },

        nextPage: () => {
            if (page >= totalPages) return;
            patchParams({page: String(page + 1)});
        },

        toggleSelect: (id: string, checked: boolean) => {
            setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
        },

        selectAllOnPage: (checked: boolean) => {
            setSelectedIds((prev) => {
                const ids = displayedPeople.map((p: any) => p.id);
                if (checked) return Array.from(new Set([...prev, ...ids]));
                return prev.filter((id) => !ids.includes(id));
            });
        },

        setRangeType: (next: RangeType) => {
            if (next === "custom") {
                patchParams({rangeType: "custom", start: startYmd, end: endYmd}, {resetPage: true});
                return;
            }
            patchParams({rangeType: next, start: null, end: null}, {resetPage: true});
        },

        setCustomDates: (start: Date, end: Date) => {
            patchParams({rangeType: "custom", start: ymd(start), end: ymd(end)}, {resetPage: true});
        },

        setSearch: (q: string) => {
            const v = q.trim();
            patchParams({search: v ? v : null}, {resetPage: true});
        },

        resetFilters: () => {
            patchParams({search: null, rangeType: "all", start: null, end: null}, {resetPage: true, replace: true});
        },
    };

    return {
        displayedPeople,
        tabTotalCount,
        totalPages,

        tab,
        page,
        pageSize,
        sortBy,
        sortDirection,
        selectedIds,

        // ВАЖНО: возвращаем наружу, чтобы FiltersBar не делал any и всегда отражал URL
        rangeType,
        search,
        startDate,
        endDate,

        actions,

        deletePersonById,
        bulkDelete,

        getPageNumbers,
    };
}
